import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import { areaNameFromSlug } from '../data/areas';

const SITE = "https://indianflatmates.co.uk";

const pageLabels = {
    "roommates": "Roommates",
    "airport-cabs": "Airport Cabs",
    "visa-and-immigration-support": "Visa & Immigration",
    "money-transfer-to-india": "Money Transfer to India",
    "uk-jobs-for-indians": "UK Jobs",
    "cv-and-interview-help": "CV & Interview Help",
    "mortgages-for-indians": "Mortgages",
    "uk-tour-trips": "UK Tour Trips",
    "indian-events-and-festivals": "Indian Festivals",
    "partner-with-us": "Partner With Us",
    "it-courses-and-certifications": "IT Courses",
    "student-admissions-uk": "Study in the UK",
    "indian-areas-in-london": "Best Areas in London",
    "top-london-attractions": "London Attractions",
    "uk-essentials-for-indians": "UK Essentials",
    "first-week-in-uk-checklist": "First Week in the UK",
    "nri-matrimonials-uk": "NRI Matrimonials",
    "indian-matrimony-uk": "NRI Matrimonials",
    "find-indian-partner-uk": "NRI Matrimonials",
};

// Trail for the current path, or null where a breadcrumb doesn't make sense
// (homepage, unknown URLs that render the 404 page).
const trailFor = (pathname) => {
    const [first, second] = pathname.replace(/^\/+|\/+$/g, '').split('/');
    if (!first) return null;
    // Room listings live on the homepage, so there's no separate "Rooms" level
    if (first === 'rooms' && second) return [{ name: `Rooms in ${areaNameFromSlug(second)}`, path: `/rooms/${second}` }];
    if (first === 'room' && second) return [{ name: "Room listing", path: pathname }];
    if (first === 'roommate' && second) return [{ name: "Roommates", path: "/roommates" }, { name: "Roommate profile", path: pathname }];
    // Matrimony aliases point at their canonical page
    const path = pageLabels[first] === "NRI Matrimonials" ? "/nri-matrimonials-uk" : `/${first}`;
    if (!second && pageLabels[first]) return [{ name: pageLabels[first], path }];
    return null;
};

function Breadcrumbs() {
    const { pathname } = useLocation();
    const trail = trailFor(pathname);
    if (!trail) return null;

    const items = [{ name: "Home", path: "/" }, ...trail];
    const schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": item.name,
            "item": `${SITE}${item.path === '/' ? '' : item.path}`,
        })),
    };

    return (
        <nav aria-label="Breadcrumb" className="container mx-auto px-4 pt-3 text-sm text-gray-500 dark:text-gray-400">
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Helmet>
            <ol className="flex flex-wrap items-center gap-1">
                {items.map((item, i) => (
                    <li key={`${item.path}-${i}`} className="flex items-center gap-1">
                        {i > 0 && <span aria-hidden="true">›</span>}
                        {i < items.length - 1 ? (
                            <Link to={item.path} className="hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline">{item.name}</Link>
                        ) : (
                            <span aria-current="page" className="text-gray-700 dark:text-gray-300">{item.name}</span>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
}

export default Breadcrumbs;
